import { useEffect, useMemo, useState } from "react";
import { getPrefilledPaginationControls } from "../../utils/pagination-helpers";
import styles from "./PaginatedList.module.scss";

interface PaginatedListProps<T> {
  data: T[];
  pageSize?: number;
  renderItem: (item: T) => React.ReactNode;
}

const PaginatedList = <T extends Record<string, any>>({ data, renderItem, pageSize = 10 }: PaginatedListProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);

  const paginationControls = useMemo(() => {
    return getPrefilledPaginationControls(currentPage, pageCount);
  }, [currentPage, pageCount]);

  const currentPageList = data.slice((currentPage - 1) * pageSize, (currentPage - 1) * pageSize + pageSize);

  useEffect(() => {
    setPageCount(Math.ceil(data.length / pageSize));
  }, [data.length, pageSize]);

  const handlePageControlClick: (pageNumber: number) => void = (_page) => {
    if (_page >= 1 && _page <= pageCount) {
      setCurrentPage(_page);
    }
  };

  const handlePageSkip: (direction: "forward" | "backward") => void = (direction) => {
    const newPage = direction === "forward" ? Math.min(currentPage + 5, pageCount) : Math.max(currentPage - 5, 1);
    setCurrentPage(newPage);
  };

  return (
    <div className={styles.container}>
      {currentPageList.map(renderItem)}
      <div className={styles.pagination}>
        <div
          onClick={() => handlePageControlClick(currentPage - 1)}
          role="button"
          className={[styles["page-control"], styles.previous, currentPage === 1 && styles.invalid].join(" ")}
        >
          <div className={styles.arrow} />
        </div>
        {paginationControls.map((control, index) =>
          control.pageNumber ? (
            <div
              key={index}
              role="button"
              className={[styles["page-control"], currentPage === control.pageNumber && styles.active].join(" ")}
              onClick={() => handlePageControlClick(control.pageNumber || 1)}
            >
              {control.pageNumber}
            </div>
          ) : (
            <div
              key={index}
              role="button"
              className={[styles.skip, styles[control.skipDirection || "forward"]].join(" ")}
              onClick={() => handlePageSkip(control.skipDirection || "forward")}
            >
              <div className={styles.dot} />
              <div className={styles.dot} />
              <div className={styles.dot} />
            </div>
          )
        )}
        <div
          onClick={() => handlePageControlClick(currentPage + 1)}
          role="button"
          className={[
            styles["page-control"],
            styles.next,
            (!pageCount || currentPage === pageCount) && styles.invalid,
          ].join(" ")}
        >
          <div className={styles.arrow} />
        </div>
      </div>

      <div className="flex gap-sm align-center justify-center smaller margin-top">
        <em className="margin-left margin-right">
          Showing {`${(currentPage - 1) * pageSize + 1} - ${currentPage * pageSize}`} of {data.length}{" "}
          {`record${data.length !== 1 ? "s" : ""}`}
        </em>
      </div>
    </div>
  );
};

export default PaginatedList;
